import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/** Ship-date rules: these weekdays only (0 = Sunday), at least this many business days out, at most this many months out. */
const SHIP_WEEKDAYS = [1, 2, 3];
const MIN_BUSINESS_DAYS = 3;
const MAX_MONTHS = 4;

/**
 * Peggy Bakes gift option: saves "This order is a gift", the recipient's name, the gift
 * message, an optional ship date and "I'll send you a card to include" as cart attributes as the shopper types, so they reach
 * the order even through express checkout buttons that skip the cart form.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} toggle
 * @property {HTMLElement} fields
 * @property {HTMLInputElement} recipient
 * @property {HTMLTextAreaElement} message
 * @property {HTMLElement} count
 * @property {HTMLInputElement} shipOn
 * @property {HTMLElement} shipOnError
 * @property {HTMLInputElement} ownCard
 * @property {HTMLElement} ownCardHelp
 *
 * @extends {Component<Refs>}
 */
class GiftOptions extends Component {
  requiredRefs = ['toggle', 'fields', 'recipient', 'message', 'count', 'shipOn', 'shipOnError', 'ownCard', 'ownCardHelp'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    const { min, max } = shipDateRange();
    this.refs.shipOn.min = toIsoDate(min);
    this.refs.shipOn.max = toIsoDate(max);

    // A date saved on an earlier visit may have gone stale: flag it and drop it from the cart.
    if ((this.refs.shipOn.value || this.refs.ownCard.checked) && !this.#checkShipDate()) this.#save();
  }

  /** Shows or hides the gift fields and saves straight away. */
  handleToggle = () => {
    const isGift = this.refs.toggle.checked;
    this.refs.fields.hidden = !isGift;
    this.refs.recipient.disabled = !isGift;
    this.refs.message.disabled = !isGift;
    this.refs.shipOn.disabled = !isGift;
    this.refs.ownCard.disabled = !isGift;
    if (isGift) this.refs.recipient.focus();
    this.#save();
  };

  /** Updates the character count and saves once the shopper pauses. */
  handleInput = debounce(() => {
    this.refs.count.textContent = String(this.refs.message.value.length);
    this.#save();
  }, 300);

  /** Checks the chosen ship date and saves it only when it follows the rules. */
  handleShipDate = () => {
    this.#checkShipDate();
    this.#save();
  };

  /** Shows the mail-in instructions; a mailed card needs a ship date. */
  handleOwnCard = () => {
    this.refs.ownCardHelp.hidden = !this.refs.ownCard.checked;
    this.#checkShipDate();
    this.#save();
  };

  /**
   * Shows an error for a date outside the rules, or a missing date when a card is being mailed. The input's custom validity also stops the
   * cart page's checkout button from submitting it.
   * @returns {boolean} True when the date is blank or allowed.
   */
  #checkShipDate() {
    const needsDate = this.refs.ownCard.checked && !this.refs.shipOn.value;
    const message = needsDate
      ? 'Please pick a ship date, so your card has time to reach us.'
      : shipDateError(this.refs.shipOn.value);
    this.refs.shipOn.setCustomValidity(message);
    this.refs.shipOnError.textContent = message;
    this.refs.shipOnError.hidden = !message;
    return !message;
  }

  async #save() {
    const isGift = this.refs.toggle.checked;
    const shipOn = this.refs.shipOn.value;
    const attributes = {
      Gift: isGift ? 'Yes' : '',
      'Gift recipient': isGift ? this.refs.recipient.value.trim() : '',
      'Gift message': isGift ? this.refs.message.value.trim() : '',
      'Ship on': isGift && shipOn && !shipDateError(shipOn) ? shipOn : '',
      'Own card': isGift && this.refs.ownCard.checked ? 'Yes' : '',
    };

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ attributes }) }),
        signal: abortController.signal,
      });
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Could not save gift details', error);
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }
}

/**
 * The earliest and latest allowed ship dates, counted from today in the shopper's time zone.
 * @returns {{ min: Date, max: Date }}
 */
function shipDateRange() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const min = new Date(today);
  let businessDays = 0;
  while (businessDays < MIN_BUSINESS_DAYS) {
    min.setDate(min.getDate() + 1);
    if (min.getDay() !== 0 && min.getDay() !== 6) businessDays++;
  }
  while (!SHIP_WEEKDAYS.includes(min.getDay())) min.setDate(min.getDate() + 1);

  const max = new Date(today);
  max.setMonth(max.getMonth() + MAX_MONTHS);

  return { min, max };
}

/**
 * @param {string} value - A YYYY-MM-DD date, or blank.
 * @returns {string} Why the date can't be used, or blank when it can.
 */
function shipDateError(value) {
  if (!value) return '';

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return 'Please choose a valid date.';

  const { min, max } = shipDateRange();
  if (date < min) return `The earliest ship date is ${formatDate(min)}.`;
  if (date > max) return `Gifts can be scheduled up to 4 months ahead (${formatDate(max)}).`;
  if (!SHIP_WEEKDAYS.includes(date.getDay())) return 'We ship gifts on Mondays, Tuesdays and Wednesdays. Please pick one of those days.';
  return '';
}

/** @param {Date} date */
function toIsoDate(date) {
  const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** @param {Date} date */
function formatDate(date) {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Cart attributes this component owns; cleared together when the cart is emptied. */
const GIFT_ATTRIBUTES = ['Gift', 'Gift recipient', 'Gift message', 'Ship on', 'Own card'];

/**
 * Shopify keeps cart attributes after the last item is removed, so a shopper who empties the
 * cart and shops again later would find "This order is a gift" already ticked. Clear them once
 * a cart change leaves the cart empty.
 * @param {Event & { promise?: Promise<any> }} event
 */
async function clearGiftWhenCartEmpties(event) {
  try {
    await event.promise;
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    if (cart.item_count > 0) return;
    if (!GIFT_ATTRIBUTES.some((name) => cart.attributes?.[name])) return;

    const attributes = Object.fromEntries(GIFT_ATTRIBUTES.map((name) => [name, '']));
    await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes }) }));
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.error('Could not reset gift details', error);
    }
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, clearGiftWhenCartEmpties);

if (!customElements.get('gift-options-component')) {
  customElements.define('gift-options-component', GiftOptions);
}
